/**
 * Renders the free-text vacancy descriptions as real prose.
 *
 * `ResponsibilityDescription` and `QualificationDescription` arrive as plain
 * text — never HTML. Surveying all 100 live vacancies on the prod gateway, the
 * editors use three conventions interchangeably, sometimes inside one field:
 *
 *   "• Assist in day-to-day operations \n• Support department tasks"
 *   "- Audit business process\n- Make recommendations to improve"
 *   "1. Manage the team\r\n2. Report to the director"
 *
 * plus ordinary unmarked lines used as headings or paragraphs, and a mix of LF
 * and CRLF. Info.cshtml poured all of that into a `<textarea disabled rows=10>`,
 * so the markers stayed as literal characters, the indentation never lined up,
 * and anything past ten lines was hidden behind an inner scrollbar.
 *
 * Here the markers are parsed and turned into the list they were imitating.
 * Text is rendered through React children, never `dangerouslySetInnerHTML` — an
 * editor typing "<script>" or "R&D" gets those characters, not markup.
 */
type Block =
  | { kind: 'bullet'; items: string[] }
  /** `heading` marks a numbered line that titles the lines beneath it — see markHeadings. */
  | { kind: 'number'; start: number; items: string[]; heading?: boolean }
  /** `under` marks the detail lines belonging to the heading above them. */
  | { kind: 'text'; lines: string[]; under?: boolean }

/** Symbol bullets may hug their text ("•Item"); dashes must not, or "e-mail" splits. */
const SYMBOL_BULLET = /^[•·▪‣●◦*]\s*/
const DASH_BULLET = /^[-–—]\s+/
const NUMBERED = /^(\d+)[.)]\s+/

function classify(line: string): { kind: Block['kind']; text: string; value?: number } {
  if (SYMBOL_BULLET.test(line)) return { kind: 'bullet', text: line.replace(SYMBOL_BULLET, '') }
  if (DASH_BULLET.test(line)) return { kind: 'bullet', text: line.replace(DASH_BULLET, '') }

  const numbered = NUMBERED.exec(line)
  if (numbered) {
    return { kind: 'number', text: line.slice(numbered[0].length), value: Number(numbered[1]) }
  }
  return { kind: 'text', text: line }
}

/** Groups consecutive lines of the same kind so three bullets become one list. */
export function parseDescription(source: string): Block[] {
  const blocks: Block[] = []

  for (const raw of source.split(/\r?\n/)) {
    const line = raw.trim()
    // A blank line ends the current run, which is how editors separate a
    // bulleted list from the paragraph that follows it.
    if (!line) {
      if (blocks.length > 0) blocks.push({ kind: 'text', lines: [] })
      continue
    }

    const { kind, text, value } = classify(line)
    if (!text) continue

    const last = blocks[blocks.length - 1]

    if (kind === 'text') {
      if (last?.kind === 'text' && last.lines.length > 0) last.lines.push(text)
      else blocks.push({ kind: 'text', lines: [text] })
      continue
    }

    if (kind === 'number') {
      // `start` carries the author's own number. Several adverts write numbered
      // HEADINGS with unmarked detail lines beneath them
      // ("1. Building Maintenance:\nOversee daily operations\n…\n2. Preventive
      // Maintenance:\n…"), which splits one logical list into several <ol>s.
      // Without `start` each of those restarts at 1, so a four-part advert
      // renders as "1." four times.
      if (last?.kind === 'number') last.items.push(text)
      else blocks.push({ kind: 'number', start: value ?? 1, items: [text] })
      continue
    }

    if (last?.kind === 'bullet') last.items.push(text)
    else blocks.push({ kind: 'bullet', items: [text] })
  }

  return markHeadings(blocks.filter((b) => (b.kind === 'text' ? b.lines.length > 0 : true)))
}

/**
 * Tells a numbered HEADING apart from a genuine numbered LIST.
 *
 * Both exist in the live data and they want opposite treatment:
 *
 *   vacancy 8  "1. Building Maintenance & Operations:"   <- heading, 4 of 16
 *              "Oversee daily operations of HVAC…"          lines numbered, each
 *              "Monitor structural integrity…"              followed by details
 *
 *   vacancy 77 "1. Supporting processes and programs…"   <- real list, 10 of 10
 *              "2. Monitoring and following up…"            lines numbered
 *
 * Emphasising every numbered line would wrongly bold the whole of vacancy 77's
 * list; emphasising none leaves vacancy 8's four section titles buried in the
 * body text. The discriminator is structural rather than a guess at the wording:
 * a numbered run of exactly ONE item that is followed by unmarked lines is
 * titling those lines. (Trailing ":" is not used — it holds for vacancy 8 but is
 * an editor's habit, not a rule.)
 */
function markHeadings(blocks: Block[]): Block[] {
  blocks.forEach((block, index) => {
    const next = blocks[index + 1]
    if (block.kind !== 'number' || block.items.length !== 1) return
    if (next?.kind !== 'text') return

    block.heading = true
    next.under = true
  })
  return blocks
}

/**
 * The per-item glyph. Both names are verified present in the bundled
 * tabler-icons.min.css — `ti-point-filled` and `ti-checklist`, the more obvious
 * choices, are NOT in this build of the font and would render as blank boxes.
 */
const MARKER_ICON = {
  arrow: 'ti ti-chevron-right',
  check: 'ti ti-circle-check',
} as const

interface DescriptionBlockProps {
  text: string | null | undefined
  marker?: keyof typeof MARKER_ICON
}

export function DescriptionBlock({ text, marker = 'arrow' }: DescriptionBlockProps): JSX.Element {
  const blocks = text ? parseDescription(text) : []

  if (blocks.length === 0) {
    return <p className="vacancy-info__empty">No information provided for this section.</p>
  }

  return (
    <div className="vacancy-info__prose">
      {blocks.map((block, index) => {
        if (block.kind === 'bullet') {
          return (
            <ul key={index} className={`vacancy-info__list vacancy-info__list--${marker}`}>
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>
                  <i className={MARKER_ICON[marker]} aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          )
        }
        if (block.kind === 'number') {
          return (
            <ol
              key={index}
              className={`vacancy-info__list vacancy-info__list--numbered${
                block.heading ? ' vacancy-info__list--lead' : ''
              }`}
              start={block.start}
            >
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{item}</li>
              ))}
            </ol>
          )
        }
        return (
          // Each source line gets its own block so the spacing matches the
          // lists; run together with <br> they read as one dense slab.
          <div
            key={index}
            className={`vacancy-info__paragraph${
              block.under ? ' vacancy-info__paragraph--under' : ''
            }`}
          >
            {block.lines.map((line, lineIndex) => (
              <span key={lineIndex} className="vacancy-info__line">
                {line}
              </span>
            ))}
          </div>
        )
      })}
    </div>
  )
}
